import { Component, OnInit, ViewEncapsulation, PLATFORM_ID, Inject, OnDestroy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MetaDefinition } from '@angular/platform-browser';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { isPlatformBrowser } from '@angular/common';


import { Product } from '../models/product.model';
import { LocationService } from '../services/location.service';
import { ProductService } from '../services/product.service';
import { AppSettings } from '../settings/app.settings';
import { AccentsUtils } from '../shared/accents-utils.service';
import { HrefLang, HrefLangDefinition } from '../shared/hreflang.service';
import { CheckingUrl } from '../shared/checking-url.service';
import { DeviceService } from '../shared/device.service';
import { SEOService } from '../shared/seo-service';

import { debounceTime } from 'rxjs/operators';
import { SearchResult, ProductSearchResult, LocationSearchResult } from '../models/search-result.model';
import { WindowResizeService } from '../shared/window-resize.service';
import { Subscription } from 'rxjs';
@Component({
  selector: 'home',
  styleUrls: ['./home.component.scss'],
  templateUrl: './home.component.html',
  encapsulation: ViewEncapsulation.Emulated
})
export class HomeComponent implements OnInit, OnDestroy {
  public isNode: boolean = false;
  public searchResults: SearchResult;
  public searchText: string;
  public cityText: any;
  public locations = [];
  public defaultLocation;
  public topProducts: Product[];
  public topLocations: any[];
  public cityObject = {};
  public textAnimation: boolean = false;
  public searchButtonAnimation: boolean = false;
  public device: string;
  public searchingLocations: FormControl = new FormControl();
  public noResults: boolean = false;
  public currentSize: number;
  public resizeSubscription: Subscription = null;

  constructor(
    @Inject(PLATFORM_ID) private platformId: string,
    private locationService: LocationService,
    private productService: ProductService,
    private router: Router,
    private translate: TranslateService,
    private activatedRoute: ActivatedRoute,
    public accentsUtils: AccentsUtils,
    private hlang: HrefLang,
    private checkUrl: CheckingUrl,
    private deviceService: DeviceService,
    private windowResizeService: WindowResizeService,
    private seoService: SEOService) {

    this.isNode = !isPlatformBrowser(this.platformId);
    this.searchingLocations.valueChanges.pipe(debounceTime(1000)).subscribe(query => {
      if (query != null && query.length >= 3) {
        this.locationService.searchLocation({ query: query }).subscribe(results => {
          this.searchResults = results.body;
          this.noResults = this.searchResults == null;
        });
      } else if (query == null || query.length == 0) {
        this.searchButtonAnimation = false;
        if (this.searchResults) {
          this.searchResults.locations = [];
          this.searchResults.products = [];
        }
      }
    });
    this.device = this.deviceService.device;
  }

  ngOnInit() {
    this.activatedRoute.params
      .subscribe(params => {
        this.findTopLocations();
        this.findTopProducts();
        this.metaCreate();
        this.createLinkForCanonicalURL();
      });
    let langs = ['es', 'en'/*,'fr','it','de'*/];
    let hreflang_: HrefLangDefinition = {
      rel: "alternate",
      href: `${AppSettings.BASE_DOMAIN}/`,
      hreflang: `x-default`
    }
    let _hlangs: HrefLangDefinition[] = [];
    this.hlang.addTags([hreflang_]);
    langs.forEach(lang => {
      let hreflang_: HrefLangDefinition = {
        rel: "alternate",
        href: `${AppSettings.BASE_DOMAIN}/${lang}/home`,
        hreflang: `${lang}`
      }
      _hlangs.push(hreflang_);
      this.hlang.addTags([hreflang_]);
    });
    let res = this.checkUrl.compareUrl(_hlangs, this.router.url.split('?')[0]);
    if (res != null) {
      if (this.isNode)
        Zone.current.get('res').redirect(301, res.url);
      else
        window.location.href = res.url;
    }
    if (!this.isNode) {
      this.currentSize = this.windowResizeService.getCurrentScreenSize();
      this.resizeSubscription = this.windowResizeService.scroll$
        .subscribe(this.handleScroll.bind(this));
    }
  }

  ngOnDestroy(): void {
    if (this.resizeSubscription) {
      this.resizeSubscription.unsubscribe();
    }
  }

  private metaCreate() {
    this.translate.get("META_TITLE_HOME").subscribe(text => {
      this.seoService.updateTitle(text);
    });
    this.translate.get("META_DESC_HOME").subscribe(text => {
      let tag: MetaDefinition = {
        name: "description",
        content: text
      };
      this.seoService.updateDescription(tag);
    });
  }

  private findTopLocations() {
    this.locationService.findTopLocations().subscribe(locations => {
      let _locations = JSON.parse(JSON.stringify(locations));
      this.locations = _locations.map((function (location) {
        location.thumbnail = `${this.deviceService.constructCloudfrontURL(AppSettings.LOCATION_IMAGES, "LARGE")}/${location.thumbnail}`;
        location.URL = `/${location.lang}/${location.id}/${this.accentsUtils.transformUrl(location.location)}`;
        return location;
      }.bind(this))).slice(0, 12);
      this.topLocations = _locations.map(l => {

        return { url: `/${l.lang}/${l.id}/${this.accentsUtils.transformUrl(l.location)}`, name: l.location }
      }).slice(12, _locations.length);
      this.defaultLocation = this.locations[0];
      this.cityObject = this.locations[0];
    });
  }

  private findTopProducts() {
    this.productService.findTopProducts().subscribe(products => {
      let _products = JSON.parse(JSON.stringify(products));
      this.topProducts = _products.map(l => {
        let language = l.textLanguage.locale.split("_")[0];
        let hrefLang = l.hreflangs[0];
        let description = l.description;
        if (l.hreflangs.length > 1) {
          hrefLang = l.hreflangs.find(hl => {
            return hl.locale.split("_")[0] == language.split("_")[0];
          });
        }
        let locationId = hrefLang.locationId;
        let locationName = hrefLang.locationName;
        let name = hrefLang.url;
        let thumbnail = `${this.deviceService.constructCloudfrontURL(AppSettings.CSS_CE_IMAGES, 'SMALL')}/${l.thumbnail}`;
        return { url: `/${language}/${locationId}/${locationName}/${l.id}/${name}`, name: l.name, thumbnail: thumbnail, description: description }
      });
    });
  }

  searchWord(search: string) {
    let productFind: boolean = false;
    if (this.searchResults) {
      if (this.searchResults.locations.length > 0) {
        if (this.searchResults.locations.length == 1 && this.searchResults.products.length == 0) {
          this.navToCity(this.searchResults.locations[0]);
        } else {
          this.searchResults.locations.map(city => {
            if (this.accentsUtils.removeAccents(city.location).toLowerCase().includes(search.toLowerCase()))
              this.navToCity(city);
          });
        }
      }
      if (this.searchResults.products.length > 0) {
        if (this.searchResults.products.length == 1) {
          this.navToProduct(this.searchResults.products[0]);
        } else {
          this.searchResults.products.map(product => {
            if (product.name == search) {
              productFind = true;
              this.navToProduct(product);
            }
          });
          if (!productFind) {
            this.navToProduct(this.searchResults.products[0])
          }
        }
      }
    }
  }

  createLinkForCanonicalURL() {
    this.seoService.createLinkForCanonicalURL('');
  }

  inputAnimation(city) {
    if (city.length < 3) {
      this.searchButtonAnimation = true;
    }
    else {
      if (!this.elementsFound()) {
        this.searchButtonAnimation = true;
      } else {
        this.searchButtonAnimation = false;
        this.searchWord(city);
      }
    }
  }

  private showDescription(location: any) {
    this.textAnimation = true;
    this.defaultLocation = location;
  }
  private resetDescription() {
    this.textAnimation = false;
  }

  private navToProduct(product: ProductSearchResult) {
    let lang = this.translate.currentLang;
    let nameCity = this.accentsUtils.transformUrl(product.location.name);
    let nameProduct = this.accentsUtils.transformUrl(product.name);

    this.router.navigate([`/${lang}/${product.location.id}/${nameCity}/${product.id}/${nameProduct}`]);
    this.searchText = "";
    this.searchingLocations.reset();
    if (this.searchResults && this.searchResults.locations)
      this.searchResults.locations = [];

    if (this.searchResults && this.searchResults.products)
      this.searchResults.products = [];
  }

  private navToCity(location: LocationSearchResult) {
    let lang = this.translate.currentLang;
    let name = this.accentsUtils.transformUrl(location.location);

    this.router.navigate([`/${lang}/${location.id}/${name}`]);

    this.searchText = "";
    this.searchingLocations.reset();
    if (this.searchResults && this.searchResults.locations)
      this.searchResults.locations = [];

    if (this.searchResults && this.searchResults.products)
      this.searchResults.products = [];
  }

  /**
   * Returns true if the search returns any result
   */
  private elementsFound(): Boolean {
    let result = false;
    if (this.searchResults) {
      if ((this.searchResults['locations'] && this.searchResults['locations'].length > 0)
        || (this.searchResults['products'] && this.searchResults['products'].length > 0)) {
        result = true;
      }
    }
    return result;
  }

  private handleScroll(resize: number): void {
    this.currentSize = resize;
  }
}
